import type { VariableSchemaEntry } from '../api/client';

/**
 * Schema-driven form.
 *
 * Renders one labelled <input> per variable in the schema, in order.
 * Required inputs get an asterisk + `required` attribute. Defaults pre-fill
 * the value. Controlled — caller owns the data dict via `value`/`onChange`.
 *
 * Component test (`SchemaForm.test.tsx`) asserts:
 *   - one input per schema entry
 *   - required entries carry the required attribute
 *   - default values pre-populate
 *   - typing fires onChange with the merged dict
 */
export function SchemaForm({
  schema,
  value,
  onChange,
  disabled,
}: {
  schema: VariableSchemaEntry[];
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
}) {
  if (schema.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        This template has no variables.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {schema.map((entry) => {
        const current = value[entry.name] ?? entry.default ?? '';
        return (
          <div key={entry.name}>
            <label className="label" htmlFor={`var-${entry.name}`}>
              {entry.label ?? entry.name}
              {entry.required ? (
                <span className="ml-1 text-red-600" aria-hidden>
                  *
                </span>
              ) : null}
            </label>
            <input
              id={`var-${entry.name}`}
              data-testid={`var-input-${entry.name}`}
              className="input mt-1"
              required={!!entry.required}
              disabled={disabled}
              value={current}
              onChange={(e) =>
                onChange({ ...value, [entry.name]: e.target.value })
              }
            />
          </div>
        );
      })}
    </div>
  );
}
